// The small vocabulary every card and action speaks: a patch of resource
// deltas applied to the civ with the clamps the rules demand, plus the
// human-readable lines that describe what just happened.

import {
  HAPPINESS_MAX, THREAT_MAX, POPULATION_MAX, HOUSING,
} from './constants';

/** How many people the town has homes for: the capital's own, plus every standing House. */
export const housingOf = (civ) => Math.min(POPULATION_MAX, HOUSING.base
  + HOUSING.perHouse * ((civ?.city?.buildings || []).filter((b) => b.type === 'house' && !b.burned).length));
import { campSpots } from './frontier';
import { rivalThreat } from './rivals';

export const RESOURCE_LABELS = {
  food: 'food', goods: 'goods', coin: 'coin', knowledge: 'knowledge', workers: 'workers',
  population: 'population', happiness: 'happiness', threat: 'threat',
  markets: 'markets', libraries: 'libraries', farms: 'fields',
};

/** Threat is what is on the map: every camp and den's strength, and every HOSTILE people's power. */
export const threatOf = (civ) => Math.min(THREAT_MAX, (civ.menaces || []).reduce((n, m) => n + m.strength, 0) + rivalThreat(civ));
export const syncThreat = (civ) => ({ ...civ, threat: threatOf(civ) });

/**
 * A `threat` delta in a card or action is applied to the MAP: minus weakens
 * the strongest thing out there (gone at 0); plus strengthens it, or, with
 * nothing out there, raises a camp at the first free shore site.
 */
export const applyThreat = (civ, delta) => {
  let menaces = [...(civ.menaces || [])];
  const lines = [];
  if (delta < 0 && menaces.length) {
    const strongest = menaces.reduce((a, b) => (b.strength > a.strength ? b : a));
    const left = strongest.strength + delta;
    menaces = left > 0
      ? menaces.map((m) => (m.id === strongest.id ? { ...m, strength: left } : m))
      : menaces.filter((m) => m.id !== strongest.id);
    lines.push(left > 0 ? `the ${strongest.kind} is weakened to ${left}` : `the ${strongest.kind} breaks up and scatters`);
  } else if (delta > 0) {
    if (menaces.length) {
      const strongest = menaces.reduce((a, b) => (b.strength > a.strength ? b : a));
      menaces = menaces.map((m) => (m.id === strongest.id ? { ...m, strength: m.strength + delta } : m));
      lines.push(`the ${strongest.kind} grows to ${strongest.strength + delta}`);
    } else {
      const taken = new Set(menaces.map((m) => `${m.q},${m.r}`));
      const spots = campSpots(civ);
      const spot = spots.find(([q, r]) => !taken.has(`${q},${r}`)) || spots[0] || [4, 0];
      menaces.push({ id: `m${(civ.menaceSeq || 0) + 1}`, kind: 'camp', q: spot[0], r: spot[1], strength: delta, age: 0 });
      lines.push(`a camp of strength ${delta} appears on the shore`);
      civ = { ...civ, menaceSeq: (civ.menaceSeq || 0) + 1 };
    }
  }
  return [syncThreat({ ...civ, menaces }), lines];
};

const CAPS = { happiness: HAPPINESS_MAX, threat: THREAT_MAX };
const capOf = (civ, key) => (key === 'population' ? housingOf(civ) : CAPS[key]);

/** Applies a delta patch to the civ. Returns [nextCiv, lines]. Never mutates. */
export const applyPatch = (civ, patch = {}) => {
  let next = { ...civ };
  const lines = [];
  Object.entries(patch).forEach(([key, delta]) => {
    if (!delta) return;
    if (key === 'threat' && next.menaces !== undefined) {
      let said;
      [next, said] = applyThreat(next, delta);
      lines.push(...said);
      return;
    }
    const before = next[key] ?? 0;
    let after = before + delta;
    if (after < 0) after = 0;
    // Never cut back past what was already there: a House that burns does
    // not evict anybody, it only stops the town growing.
    const cap = capOf(next, key);
    if (cap !== undefined && delta > 0 && after > Math.max(cap, before)) after = Math.max(cap, before);
    next[key] = after;
    const actual = after - before;
    if (actual !== 0) lines.push(`${actual > 0 ? '+' : ''}${actual} ${RESOURCE_LABELS[key] || key}`);
  });
  return [next, lines];
};

// "1 workers" was on every card in the Hall. Only the countable labels take
// a plural; food, goods, coin and knowledge are already mass nouns.
const PLURALISE = { workers: 'worker', people: 'person' };

/** Can the civ pay this cost right now? Costs are positive numbers. */
export const canAfford = (civ, cost = {}) => Object.entries(cost).every(([key, n]) => (civ[key] ?? 0) >= n);

export const missingFor = (civ, cost = {}) => Object.entries(cost)
  .filter(([key, n]) => (civ[key] ?? 0) < n)
  .map(([key, n]) => {
    const short = n - (civ[key] ?? 0);
    const label = RESOURCE_LABELS[key] || key;
    // "Needs 1 more workers" was on every greyed button.
    return `${short} more ${short === 1 && PLURALISE[label] ? PLURALISE[label] : label}`;
  });

/** Turns a cost object into its negative patch. */
export const costPatch = (cost = {}) => Object.fromEntries(
  Object.entries(cost).map(([key, n]) => [key, -n]),
);

export const describeCost = (cost = {}) => Object.entries(cost)
  .filter(([, n]) => n > 0)
  .map(([key, n]) => {
    const label = RESOURCE_LABELS[key] || key;
    if (n === 1 && PLURALISE[label]) return `1 ${PLURALISE[label]}`;
    return `${n} ${label}`;
  })
  .join(', ');

const KEY_FOR_LABEL = Object.fromEntries(Object.entries(RESOURCE_LABELS).map(([k, v]) => [v, k]));

/**
 * "+4 coin" → "+4 coin (now 5)". Playtest 3: "we need a lot more detail in
 * the log, we really can't tell what's happening" - a delta without the
 * total it landed on is the half of the story nobody can do arithmetic on
 * mid-game. Lines that are not resource deltas pass through untouched.
 */
export const withTotals = (civ, lines = []) => lines.map((line) => {
  const m = /^([+-]\d+) (.+)$/.exec(line);
  const key = m && KEY_FOR_LABEL[m[2]];
  if (!key || civ[key] === undefined) return line;
  return `${line} (now ${civ[key]})`;
});

/**
 * The resources a board shows as pips. `workers` is deliberately out: during
 * the day `civ.workers` is the ROLE's hands and gets handed back at the end
 * of an action, so a before/after diff on it says nothing true.
 */
export const COUNTED = ['food', 'goods', 'coin', 'knowledge', 'population', 'happiness', 'threat'];
export const countsOf = (civ = {}) => Object.fromEntries(COUNTED.map((k) => [k, civ[k] ?? 0]));
/** What changed between two snapshots, as a patch of non-zero deltas. */
export const changeBetween = (before = {}, after = {}) => Object.fromEntries(
  COUNTED.map((k) => [k, (after[k] ?? 0) - (before[k] ?? 0)]).filter(([, n]) => n !== 0),
);

export const describeGain = (patch = {}) => Object.entries(patch)
  .filter(([, n]) => n)
  .map(([key, n]) => `${n > 0 ? '+' : ''}${n} ${RESOURCE_LABELS[key] || key}`)
  .join(', ');
